import moment from "moment";
import React, { useCallback, useState } from "react";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import EventTimeline from "../../components/Shared/EventTimeline";
import { LabelValues } from "../../components/Shared/EventTimeline/eventTimeline.utils";
import ButtonNew from "../../components/Core/Button/ButtonNew";
import Consultant from "../../models/Consultant";
import {
	NOTIFICATION_SENT,
	NotificationChannel,
	NotificationSentArgs,
	TimelineEntry,
} from "../../models/EventTimeline";
import { useConsultantHistory } from "../../networking/consultant.hooks";
import { useConsultantManagersByOrg } from "../../networking/internal-user.hooks";
import { useOrganisation } from "../../networking/organisation.hooks";
import { useSubsidiaries } from "../../networking/subsidiary.hooks";
import { getCompetencesLabels } from "../../utils/competence.utils";

/**
 * Dot colour per event type. Explicit mapping (not string building) so
 * Tailwind's static scan picks the classes up. Grouped by what the admin is
 * scanning for: lifecycle (blue/red), credentials (purple/amber), money
 * (emerald), system (grey).
 */
const CONSULTANT_DOT_CLASS_BY_TYPE: { [type: string]: string } = {
	CONSULTANT_CREATED: "bg-blue-500",
	CONSULTANT_UPDATED: "bg-blue-400",
	CONSULTANT_ACTIVATED: "bg-emerald-500",
	CONSULTANT_DEACTIVATED: "bg-red-500",
	CONSULTANT_BLOCKED: "bg-red-500",
	CONSULTANT_UNBLOCKED: "bg-emerald-500",
	SALARY_CHANGED: "bg-emerald-600",
	TAG_ADDED: "bg-indigo-500",
	TAG_REMOVED: "bg-indigo-400",
	TAG_EXPIRED: "bg-gray-400",
	TAG_RENEWED: "bg-indigo-500",
	CERTIFICATION_ADDED: "bg-purple-500",
	CERTIFICATION_REMOVED: "bg-purple-400",
	CERTIFICATION_EXPIRY_CHANGED: "bg-purple-600",
	COMPETENCE_ADDED: "bg-amber-500",
	COMPETENCE_REMOVED: "bg-amber-600",
	COMPETENCE_SUPPLEMENT_CHANGED: "bg-amber-400",
	DOCUMENT_ADDED: "bg-teal-500",
	DOCUMENT_UPDATED: "bg-teal-400",
	DOCUMENT_REMOVED: "bg-teal-600",
	MANAGER_TRANSFERRED: "bg-indigo-600",
	RECRUITED: "bg-blue-600",
	EMPLOYMENT_STARTED_SET: "bg-gray-400",
	EMPLOYMENT_CERTIFICATE_SENT: "bg-gray-400",
	PASSWORD_SET: "bg-purple-500",
	NOTIFICATION_SENT: "bg-sky-500",
};

/** Characters of the message body shown before "Visa mer" takes over. */
const BODY_PREVIEW_LENGTH = 100;

interface Props {
	consultant: Consultant;
}

/**
 * "Händelser" tab — the event timeline for a consultant. Fetches on tab
 * activation (RouteTabs mounts only the active tab's content).
 *
 * Taxonomy keys in the event payloads are resolved against the live
 * organisation here, falling back to the name snapshotted when the event was
 * written — so a renamed type reads correctly and a deleted one still reads.
 */
const ConsultantHistoryTab: React.FC<Props> = ({ consultant }) => {
	const { t } = useTranslation();

	// Sent notifications are merged in unconditionally — the opt-in toggle
	// they shipped behind was dropped after testing.
	const { entries, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage } =
		useConsultantHistory({ userId: consultant.id, includeMessages: true });

	const { data: organisation } = useOrganisation();
	const { subsidiaries } = useSubsidiaries();
	const { managers } = useConsultantManagersByOrg(consultant.profile?.organisation?.id);

	const getLabelValues = useCallback(
		(entry: TimelineEntry, t: TFunction): LabelValues => {
			// Message rows carry a template pair rather than the key/name shape the
			// taxonomy events below use, so they resolve on their own.
			if (entry.type === NOTIFICATION_SENT) {
				const { template, channels } = (entry.args ?? {}) as Partial<NotificationSentArgs>;

				return {
					name: template?.humanReadableName || template?.name || "",
					channels: formatChannels(channels, t),
				};
			}

			const args = (entry.args ?? {}) as {
				key?: string;
				name?: string;
				branch?: string;
				service?: string;
				skill?: string;
				from?: unknown;
				to?: unknown;
				customer?: { id: string; name?: string };
			};

			// All three levels resolved against the live tree, so a renamed branch,
			// service or skill reads correctly. The snapshot in `args.name` is the
			// fallback for entries whose taxonomy has since been deleted.
			const competenceLabel = (competence: {
				branch?: string;
				service?: string;
				skill?: string;
			}) => {
				if (!competence.branch || !competence.service || !organisation?.competences)
					return undefined;

				const { branch, service, skills } = getCompetencesLabels(organisation.competences, {
					branch: competence.branch,
					service: competence.service,
					skills: competence.skill ? [competence.skill] : [],
				});

				if (!branch || !service) return undefined;

				return [branch, service, ...skills].join(" / ");
			};

			const resolveName = () => {
				if (entry.type.startsWith("TAG_"))
					return (
						organisation?.consultantTagTypes?.find((type) => type.key === args.key)?.name?.sv ??
						args.name ??
						args.key
					);

				if (entry.type.startsWith("CERTIFICATION_"))
					return (
						organisation?.certificationTypes?.find((type) => type.key === args.key)?.name?.sv ??
						args.name ??
						args.key
					);

				if (entry.type.startsWith("COMPETENCE_")) return competenceLabel(args) ?? args.name;

				if (entry.type.startsWith("CONSULTANT_")) return args.customer?.name ?? args.customer?.id;

				return args.name;
			};

			return {
				name: resolveName() ?? "",
				from: formatSide(entry.type, args.from, managers, subsidiaries),
				to: formatSide(entry.type, args.to, managers, subsidiaries),
			};
		},
		[organisation, managers, subsidiaries]
	);

	return (
		<div className="max-w-2xl py-4">
			{isError && (
				<div className="py-8 text-center text-sm text-red-600">{t("eventTimeline.loadError")}</div>
			)}

			{!isError && !isLoading && entries && (
				<>
					<EventTimeline
						entries={entries}
						i18nPrefix="eventTimeline.consultant"
						dotClassByType={CONSULTANT_DOT_CLASS_BY_TYPE}
						getLabelValues={getLabelValues}
						renderExtraRows={renderMessageRows}
						emptyState={t("eventTimeline.noEvents")}
					/>

					{hasNextPage && (
						<div className="mt-6 flex justify-center">
							<ButtonNew
								variant="neutral"
								onClick={() => fetchNextPage()}
								disabled={isFetchingNextPage}
							>
								{t("eventTimeline.showMore")}
							</ButtonNew>
						</div>
					)}
				</>
			)}
		</div>
	);
};

/**
 * Channel list for a message label, parenthesised — "(push, SMS)" — or an empty
 * string when the backend attributed none (it never guesses a channel it cannot
 * map back to the recipient). The brackets live here rather than in the i18n
 * string so the sentence does not end in a stray "()".
 */
function formatChannels(channels: NotificationChannel[] | undefined, t: TFunction): string {
	if (!channels?.length) return "";

	const names = channels.map((channel) =>
		t(`eventTimeline.channel.${channel}` as any, { defaultValue: channel })
	);

	return ` (${names.join(", ")})`;
}

/** Subject and body under a message row; nothing at all for other event types. */
function renderMessageRows(entry: TimelineEntry): React.ReactNode {
	if (entry.type !== NOTIFICATION_SENT) return null;

	const { subject, body } = (entry.args ?? {}) as Partial<NotificationSentArgs>;

	if (!subject && !body) return null;

	return <MessageRows subject={subject} body={body} />;
}

/**
 * The point of the feature for support: what was actually sent. The body is
 * stored in full and shown truncated, since a mail body can run for screens.
 */
const MessageRows: React.FC<{ subject?: string; body?: string }> = ({ subject, body }) => {
	const { t } = useTranslation();
	const [isExpanded, setExpanded] = useState(false);

	const isTruncatable = !!body && body.length > BODY_PREVIEW_LENGTH;
	const shownBody =
		body && isTruncatable && !isExpanded
			? `${body.slice(0, BODY_PREVIEW_LENGTH).trimEnd()}…`
			: body;

	return (
		<div className="mt-1.5 text-xs text-gray-600">
			{subject && <div className="font-medium">{subject}</div>}

			{shownBody && <div className="whitespace-pre-line">{shownBody}</div>}

			{isTruncatable && (
				<button
					type="button"
					className="mt-0.5 border-none bg-transparent p-0 text-xs text-action underline"
					onClick={() => setExpanded(!isExpanded)}
				>
					{t(isExpanded ? "eventTimeline.showLessBody" : "eventTimeline.showMoreBody")}
				</button>
			)}
		</div>
	);
};

/**
 * One side of a from/to pair. Ids are resolved to names where the timeline
 * knows how (managers, subsidiaries); dates are formatted; everything else is
 * shown as-is.
 */
function formatSide(
	type: string,
	value: unknown,
	managers: { id?: string; firstName?: string; lastName?: string }[],
	subsidiaries: { id: string; name: string }[]
): string {
	if (value === undefined || value === null || value === "") return "–";

	if (type === "MANAGER_TRANSFERRED") {
		const manager = managers.find(({ id }) => id === value);

		return manager
			? [manager.firstName, manager.lastName].filter(Boolean).join(" ")
			: String(value);
	}

	if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value))
		// Timed tags run on hours, so a renewal needs the time of day to be readable.
		return moment(value).format(type === "TAG_RENEWED" ? "D MMM HH:mm" : "D MMM YYYY");

	const subsidiary = subsidiaries.find(({ id }) => id === value);

	return subsidiary ? subsidiary.name : String(value);
}

export default ConsultantHistoryTab;
